import { apiClient } from './axios';

export interface Webhook {
  id: string;
  name: string;
  url: string;
  secret: string | null;
  headers: Record<string, string> | null;
  events: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WebhookDelivery {
  id: string;
  webhook_id: string;
  event: string;
  payload: Record<string, unknown>;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  response_status_code: number | null;
  response_body: string | null;
  attempts: number;
  next_retry_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateWebhookDto {
  name: string;
  url: string;
  secret?: string;
  headers?: Record<string, string> | null;
  events?: string[];
  active?: boolean;
}

export interface UpdateWebhookDto {
  name?: string;
  url?: string;
  secret?: string | null;
  headers?: Record<string, string> | null;
  events?: string[];
  active?: boolean;
}

export interface PaginatedDeliveries {
  data: WebhookDelivery[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const webhooksApi = {
  getWebhooks: async () => {
    const response = await apiClient.get<Webhook[]>('/webhooks');
    return response.data;
  },

  getWebhook: async (id: string) => {
    const response = await apiClient.get<Webhook>(`/webhooks/${id}`);
    return response.data;
  },

  createWebhook: async (data: CreateWebhookDto) => {
    const response = await apiClient.post<Webhook>('/webhooks', data);
    return response.data;
  },

  updateWebhook: async (id: string, data: UpdateWebhookDto) => {
    const response = await apiClient.patch<Webhook>(`/webhooks/${id}`, data);
    return response.data;
  },

  deleteWebhook: async (id: string) => {
    await apiClient.delete(`/webhooks/${id}`);
  },

  getDeliveries: async (webhookId: string, page = 1, limit = 20) => {
    const response = await apiClient.get<PaginatedDeliveries>(`/webhooks/${webhookId}/deliveries`, {
      params: { page, limit },
    });
    return response.data;
  },

  triggerWebhook: async (
    id: string,
    body: {
      dataType: 'availability_data' | 'lead_data' | 'shift_data';
      fromDate: string;
      toDate: string;
      confirmedOnly?: boolean;
    },
  ) => {
    const response = await apiClient.post<{
      deliveryId: string;
      status: string;
      callsFound: number;
    }>(`/webhooks/${id}/trigger`, body);
    return response.data;
  },
};
